import { has, sum } from 'lodash'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'

import VerticalBarChart from '@/pages/SpaceAnalyticsPage/charts/VerticalBarChart'
import pageMessages from '@/pages/SpaceAnalyticsPage/messages'
import EngagementIcon from '@/pages/SpaceAnalyticsPage/util/icons/EngagementIcon'
import TotalSentIcon from '@/pages/SpaceAnalyticsPage/util/icons/TotalSentIcon'
import UniqueViewsIcon from '@/pages/SpaceAnalyticsPage/util/icons/UniqueViewsIcon'
import MetricHorizontalSection from '@/pages/SpaceAnalyticsPage/util/MetricHorizontalSection'

import messages from './messages'

function PublisherSection(props: any) {
  const intl = useIntl()

  const calculateEngagement = ({ urlVisit, detailViewed }: any) => {
    return sum([urlVisit, detailViewed])
  }

  const hasData = (metrics: any) => {
    if (has(metrics, 'urlVisit') && has(metrics, 'detailViewed')) return true
    return false
  }

  const formatChartData = ({ urlVisit, detailViewed }: any) => {
    return [
      {
        name: intl.formatMessage(messages.openedDetail),
        value: detailViewed
      },
      {
        name: intl.formatMessage(messages.clickedLinks),
        value: urlVisit
      }
    ]
  }

  const { publisherMetrics, previousPublisherMetrics, isDateRangeQuery, renderDifferences, isWorkshopNotification } =
    props
  if (!hasData(publisherMetrics)) {
    return (
      <div className="analytics-section">
        <div className="sub-section-wrapper">
          <div className="sub-section">
            <div className="metric-card-subheader">
              <h3>
                {isWorkshopNotification ? (
                  <FormattedMessage {...messages.workshopMetricsTitle} />
                ) : (
                  <FormattedMessage {...messages.metricsTitle} />
                )}
              </h3>
            </div>
            <p className="flex-center">
              <FormattedMessage {...pageMessages.noActivityMetrics} />
            </p>
          </div>
        </div>
      </div>
    )
  }

  const engagement = calculateEngagement(publisherMetrics)
  const chartData = formatChartData(publisherMetrics)
  let previousEngagement

  if (previousPublisherMetrics) previousEngagement = calculateEngagement(previousPublisherMetrics)

  return (
    <div className="analytics-section">
      <div className="sub-section-wrapper">
        <div className="sub-section">
          <div className="metric-card-subheader">
            <h3>
              {isWorkshopNotification ? (
                <FormattedMessage {...messages.workshopMetricsTitle} />
              ) : (
                <FormattedMessage {...messages.metricsTitle} />
              )}
            </h3>
            <span className="sub-text">
              {isWorkshopNotification ? (
                <FormattedMessage {...messages.workshopMetricsDescription} />
              ) : (
                <FormattedMessage {...messages.metricsDescription} />
              )}
            </span>
          </div>
          <div className="metric-horizontal-holder">
            <MetricHorizontalSection
              icon={<TotalSentIcon />}
              label={<FormattedMessage {...messages.totalSent} />}
              value={publisherMetrics.sentNotifications}
              differenceValue={!isDateRangeQuery && previousPublisherMetrics.sentNotifications}
              renderDifferenceValue={renderDifferences}
            />
            <MetricHorizontalSection
              icon={<UniqueViewsIcon />}
              label={<FormattedMessage {...messages.uniqueViews} />}
              value={publisherMetrics.viewedNotifications}
              differenceValue={!isDateRangeQuery && previousPublisherMetrics.viewedNotifications}
              renderDifferenceValue={renderDifferences}
            />
            <MetricHorizontalSection
              icon={<EngagementIcon />}
              label={<FormattedMessage {...messages.engagement} />}
              value={engagement}
              differenceValue={!isDateRangeQuery && previousEngagement}
              renderDifferenceValue={renderDifferences}
            />
          </div>
        </div>
        <div className="divider" />
        <div className="sub-section">
          <div className="metric-card-subheader">
            <h3>
              {isWorkshopNotification ? (
                <FormattedMessage {...messages.workshopChartTitle} />
              ) : (
                <FormattedMessage {...messages.chartTitle} />
              )}
            </h3>
            <span className="sub-text">
              {isWorkshopNotification ? (
                <FormattedMessage {...messages.workshopChartDescription} />
              ) : (
                <FormattedMessage {...messages.chartDescription} />
              )}
            </span>
          </div>
          <VerticalBarChart data={chartData} />
        </div>
      </div>
    </div>
  )
}

PublisherSection.displayName = 'PublisherSection'

PublisherSection.propTypes = {
  publisherMetrics: PropTypes.object,
  previousPublisherMetrics: PropTypes.object,
  isDateRangeQuery: PropTypes.bool.isRequired,
  renderDifferences: PropTypes.bool,
  isWorkshopNotification: PropTypes.bool
}

PublisherSection.defaultProps = {
  publisherMetrics: {},
  previousPublisherMetrics: {
    sentNotifications: 0,
    viewedNotifications: 0,
    urlVisit: 0,
    detailViewed: 0
  },
  renderDifferences: true,
  isWorkshopNotification: false
}

export default PublisherSection
